import React from "react";

/**
 * PhotoCollage — masonry tile layout
 *
 * Every image renders at its natural aspect ratio (no cropping).
 * Images are distributed across columns and tile together naturally.
 * Portrait photos stay portrait, landscape stays landscape.
 *
 * Props:
 *   photos   — array of { src: string, alt?: string }
 *   columns  — object mapping breakpoint to column count
 *              default: { sm: 2, md: 3, lg: 3 }
 *   gap      — gap between tiles in px, default 6
 *   rounded  — border-radius Tailwind class, default "rounded-xl"
 *
 * Usage:
 *   <PhotoCollage photos={[{ src: "/img/1.jpg", alt: "Beach" }, ...]} />
 *   <PhotoCollage photos={photos} columns={{ sm: 2, md: 3, lg: 4 }} gap={8} />
 */
export default function PhotoCollage({
  photos = [],
  columns = { sm: 2, md: 3, lg: 5 },
  gap = 6,
  rounded = "",
}) {
  if (!photos.length) return null;

  /**
   * Distribute photos into N columns using a shortest-column-first
   * approach. Without measuring actual rendered heights we track
   * estimated "weight" (portrait = 1.5, landscape = 0.7, unknown = 1)
   * so columns stay visually balanced.
   */
  const distribute = (items, n) => {
    const cols = Array.from({ length: n }, () => []);
    const heights = new Array(n).fill(0);

    items.forEach((item) => {
      // Pick the currently shortest column
      const shortest = heights.indexOf(Math.min(...heights));
      cols[shortest].push(item);
      // Estimate weight: use provided aspect ratio, or guess portrait (0.67)
      const ar = item.aspectRatio ?? 0.67;
      heights[shortest] += 1 / ar; // taller images add more "height units"
    });

    return cols;
  };

  const colCountSm = columns.sm ?? 2;
  const colCountMd = columns.md ?? 3;
  const colCountLg = columns.lg ?? 3;

  return (
    <>
      <style>{`
        .pc-row { display: flex; align-items: flex-start; width: 100%; gap: ${gap}px; }
        .pc-col  { flex: 1 1 0%; min-width: 0; display: flex; flex-direction: column; gap: ${gap}px; }
        .pc-item { width: 100%; display: block; overflow: hidden; }
        .pc-item img { width: 100%; height: auto; display: block; }
      `}</style>

      {/* Mobile */}
      <div className="block sm:hidden my-4">
        <MasonryColumns
          photos={photos}
          n={colCountSm}
          distribute={distribute}
          rounded={rounded}
        />
      </div>

      {/* Tablet */}
      <div className="hidden sm:block lg:hidden my-4">
        <MasonryColumns
          photos={photos}
          n={colCountMd}
          distribute={distribute}
          rounded={rounded}
        />
      </div>

      {/* Desktop */}
      <div className="hidden lg:block my-4">
        <MasonryColumns
          photos={photos}
          n={colCountLg}
          distribute={distribute}
          rounded={rounded}
        />
      </div>
    </>
  );
}

function MasonryColumns({ photos, n, distribute, rounded }) {
  const cols = distribute(photos, n);
  return (
    <div className="pc-row">
      {cols.map((col, ci) => (
        <div key={ci} className="pc-col">
          {col.map((photo, pi) => (
            <div key={pi} className={`pc-item ${rounded}`}>
              <img
                src={photo.src}
                alt={photo.alt ?? ""}
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
